import PropTypes from 'prop-types';
import { Tooltip } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import CorporationUtils from '@/common/utils/CorporationUtils';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { contentDetailOnDemandAtom } from '@/state/atom/translationAtom';
import {
    requestContentDetailOnDemandTranslation,
    setContentDetailOnDemandActiveView,
    setContentDetailOnDemandTicketMode
} from '@/component/multiLanguage/handlers/DetailContentOnDemandHandler';
import { trI18n } from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import Constant from '@/common/constant/Constant';
import TranslationConstant from '@/common/constant/TranslationConstant';

import './DetailContentOnDemandControl.css';
import DataChangePleaseReloadModal from '@/component/common/modal/DataChangePleaseReloadModal';

const { ON_DEMAND_TRANSLATION_STATUS, ON_DEMAND_TRANSLATION_VIEW } = TranslationConstant;

DetailContentOnDemandControl.propTypes = {
    contentMode: PropTypes.string,
    contentId: PropTypes.string,
    className: PropTypes.string,
};

export default function DetailContentOnDemandControl({
    contentMode = Constant.DISPLAY_MODE.VIEW,
    contentId,
    className = '',
}) {
    useTranslationSubscription();
    const corporationDetail = useRecoilValue(corporationAtom);
    const [isOpenModalReloadPage, setIsOpenModalReloadPage] = useState(false);
    const [contentDetailOnDemand, setContentDetailOnDemand] = useRecoilState(contentDetailOnDemandAtom);

    const translationRecord = contentId ? contentDetailOnDemand[contentId] : null;
    const translateStatus = translationRecord?.status || ON_DEMAND_TRANSLATION_STATUS.IDLE;
    const activeView = translationRecord?.activeView || ON_DEMAND_TRANSLATION_VIEW.ORIGINAL;

    useEffect(() => {
        setContentDetailOnDemandTicketMode(setContentDetailOnDemand, contentId, contentMode);

        return () => {
            setContentDetailOnDemandTicketMode(
                setContentDetailOnDemand,
                contentId,
                Constant.DISPLAY_MODE.VIEW
            );
        };
    }, [contentMode, contentId, setContentDetailOnDemand]);

    // Hide entirely when the multi-language feature is off for this organization.
    if (!CorporationUtils.isEnableSettingMultiLanguage(corporationDetail)) return null;

    // Hide when the control is used in the edit mode.
    if (contentMode === Constant.DISPLAY_MODE.EDIT) return null;

    const isLoading = translateStatus === ON_DEMAND_TRANSLATION_STATUS.LOADING;
    const isTranslated = translateStatus === ON_DEMAND_TRANSLATION_STATUS.TRANSLATED;
    const isShowingTranslation = activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION;

    const handleToggleOnDemand = () => {
        if (isLoading) return;

        // Turning OFF -> show original in place (no re-translate).
        if (isShowingTranslation) {
            setContentDetailOnDemandActiveView(
                setContentDetailOnDemand,
                contentId,
                ON_DEMAND_TRANSLATION_VIEW.ORIGINAL
            );
            return;
        }

        // Turning ON when a translation already exists in this session -> just show
        // it (do NOT call the API again).
        if (isTranslated) {
            setContentDetailOnDemandActiveView(
                setContentDetailOnDemand,
                contentId,
                ON_DEMAND_TRANSLATION_VIEW.TRANSLATION
            );
            return;
        }

        // First ON (IDLE/ERROR): trigger the translation. On success the handler
        // switches activeView to TRANSLATION (toggle stays ON).
        requestContentDetailOnDemandTranslation({
            setContentDetailOnDemand,
            ticketId: contentId,
            record: translationRecord,
            setIsOpenModalReloadPage
        });
    };

    const toggleId = `ondemand-translation-toggle-${contentId}`;

    return (
        <div className={`detail-ondemand-translation ${className}`.trim()} data-cy="detail-ondemand-translation">
            <span className="detail-ondemand-translation-label">
                {trI18n('CM-ONDEMAND-TRANSLATION-TOGGLE-LABEL')}
            </span>

            <div className="switch-check">
                <input
                    name="onDemandTranslation"
                    id={toggleId}
                    type="checkbox"
                    checked={isShowingTranslation}
                    disabled={isLoading}
                    onChange={handleToggleOnDemand}
                    data-cy="detail-ondemand-translation-toggle"
                />
                <label htmlFor={toggleId}></label>
            </div>

            {isLoading && (
                <Tooltip title={trI18n('CM-ONDEMAND-TRANSLATION-LOADING-TOOLTIP')} arrow placement="right-end">
                    <img
                        className="option-icon icon-loading"
                        src={`/assets/image/icon-loading.svg`}
                        alt="icon-loading"
                        data-cy="detail-ondemand-translation-loading"
                    />
                </Tooltip>
            )}

            <DataChangePleaseReloadModal
                isOpen={isOpenModalReloadPage}
                setIsOpen={setIsOpenModalReloadPage}
            />
        </div>
    );
};
